import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { RaceCard } from "@/components/RaceCard";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { dateBadge, sportName } from "@/lib/helpers";
import { SportIcon } from "@/components/SportIcon";
import { Heart, CalendarDays, MapPin, Activity, ArrowRight, Settings, Loader2 } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [saved, setSaved] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSaved = () => api.get("/saved").then((s) => setSaved(s.data.races)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/saved"),
      api.get(`/races?${user?.preferred_sports?.[0] ? `sport=${user.preferred_sports[0]}` : ""}`),
    ]).then(([s, r]) => { setSaved(s.data.races); setRecommended(r.data.races.slice(0, 4)); }).finally(() => setLoading(false));
  }, [user]);

  const upcoming = [...saved].sort((a, b) => a.date.localeCompare(b.date)).slice(0, 4);

  const stats = [
    { icon: CalendarDays, label: "Upcoming Races", value: upcoming.length, to: "/calendar", link: "View Calendar" },
    { icon: Heart, label: "Saved Races", value: saved.length, to: "/my-races", link: "View My Races" },
    { icon: Activity, label: "Primary Sport", value: sportName(user?.preferred_sports?.[0] || "running"), to: "/profile", link: "Edit Preferences" },
    { icon: MapPin, label: "Your Location", value: user?.location || "Not set", to: "/profile", link: "Update" },
  ];

  return (
    <Layout>
      <div className="relative bg-[#0a1420] overflow-hidden">
        <img src="https://images.pexels.com/photos/29840338/pexels-photo-29840338.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl text-white leading-none">Hi {user?.name?.split(" ")[0]},<br /><span className="text-[#22d3ee]">Ready for your next race?</span></h1>
          <p className="text-slate-300 mt-3">Discover. Plan. Train. Race. Repeat.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                <Icon size={22} className="text-[#06b6d4]" />
                <div className="font-display font-black text-2xl text-slate-800 mt-2 truncate">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
                <Link to={s.to} className="text-xs text-[#06b6d4] font-semibold mt-2 inline-flex items-center gap-1 hover:underline">{s.link} <ArrowRight size={12} /></Link>
              </div>
            );
          })}
        </div>

        <div className="flex items-end justify-between mt-12 mb-5">
          <h2 className="font-display font-extrabold uppercase text-2xl text-slate-800">Your Upcoming Races</h2>
          <Link to="/my-races" className="text-[#06b6d4] font-semibold text-sm hover:underline inline-flex items-center gap-1">View All <ArrowRight size={14} /></Link>
        </div>
        {loading ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={28} /></div> :
          upcoming.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
              <p className="text-slate-500">No saved races yet. <Link to="/races" className="text-[#06b6d4] font-semibold">Find your next race →</Link></p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{upcoming.map((r) => <RaceCard key={r.id} race={r} onSaveChange={loadSaved} />)}</div>
          )}

        <div className="flex items-end justify-between mt-12 mb-5">
          <div><h2 className="font-display font-extrabold uppercase text-2xl text-slate-800">Recommended for You</h2>
          <p className="text-slate-500 text-sm">Based on your interests</p></div>
          <Link to="/races" className="text-[#06b6d4] font-semibold text-sm hover:underline inline-flex items-center gap-1">View All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{recommended.map((r) => <RaceCard key={r.id} race={r} onSaveChange={loadSaved} />)}</div>
      </div>
    </Layout>
  );
}
